/**
 * NotificationsPanel.jsx
 * 
 * Dropdown panel showing quick access to urgent items.
 * Displays urgent concerns and pending reviews in a compact format.
 * 
 * Usage:
 *   <NotificationsPanel isOpen={isOpen} onClose={onClose} />
 */

import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  X, 
  AlertCircle, 
  MessageSquare, 
  Clock,
  ChevronRight,
  User,
  Check
} from 'lucide-react'
import { mdApi } from '../api/mdApi'
import './NotificationsPanel.css'

// ============================================================================
// Constants
// ============================================================================

/**
 * Severity configuration for visual indicators
 */
const SEVERITY_CONFIG = {
  urgent: { icon: '🔴', color: '#DC3545', label: 'Urgent' },
  high: { icon: '🟠', color: '#FD7E14', label: 'High' },
  medium: { icon: '🟡', color: '#FFC107', label: 'Medium' },
  low: { icon: '🟢', color: '#28A745', label: 'Low' },
}

/** Maximum items to show per section */
const MAX_ITEMS_PER_SECTION = 5

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format relative time for compact display
 */
const formatTime = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  
  if (diffMins < 1) return 'now'
  if (diffMins < 60) return `${diffMins}m`
  if (diffHours < 24) return `${diffHours}h`
  return `${Math.floor(diffHours / 24)}d`
}

// ============================================================================
// Sub-Components
// ============================================================================

/**
 * NotificationItem - Individual notification row
 */
const NotificationItem = ({ 
  icon, 
  iconColor, 
  title, 
  subtitle, 
  time, 
  onClick 
}) => (
  <button className="notification-item" onClick={onClick}>
    <span className="notification-icon" style={{ color: iconColor }}>
      {icon}
    </span>
    <div className="notification-content">
      <div className="notification-title">{title}</div>
      <div className="notification-subtitle">{subtitle}</div>
    </div>
    <div className="notification-meta">
      <span className="notification-time">{time}</span>
      <ChevronRight size={14} className="notification-arrow" />
    </div>
  </button>
)

/**
 * SectionHeader - Section title with count
 */
const SectionHeader = ({ title, count }) => (
  <div className="notification-section-header">
    <span className="section-title">{title}</span>
    {count > 0 && <span className="section-count">{count}</span>}
  </div>
)

// ============================================================================
// Main Component
// ============================================================================

function NotificationsPanel({ isOpen, onClose }) {
  const navigate = useNavigate()
  const panelRef = useRef(null)
  
  // State
  const [urgentConcerns, setUrgentConcerns] = useState([])
  const [highConcerns, setHighConcerns] = useState([])
  const [pendingReviews, setPendingReviews] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  // ============================================================================
  // Data Fetching
  // ============================================================================
  
  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const [urgent, high, reviews] = await Promise.all([
        mdApi.getPendingConcerns('urgent'),
        mdApi.getPendingConcerns('high'),
        mdApi.getPendingReviews(),
      ])
      
      setUrgentConcerns(urgent || [])
      setHighConcerns(high || [])
      setPendingReviews(reviews || [])
    } catch (err) {
      console.error('Error loading notifications:', err)
      setError('Failed to load notifications')
    } finally {
      setLoading(false)
    }
  }, [])
  
  // Load data when panel opens
  useEffect(() => {
    if (isOpen) {
      loadNotifications()
    }
  }, [isOpen, loadNotifications])
  
  // ============================================================================
  // Click Outside Handler
  // ============================================================================
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        // Check if click is on the bell icon (parent handles this)
        const bellButton = event.target.closest('.notification-bell-button')
        if (!bellButton) {
          onClose()
        }
      }
    }
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])
  
  // ============================================================================
  // Keyboard Handler
  // ============================================================================
  
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])
  
  // ============================================================================
  // Event Handlers
  // ============================================================================
  
  const handleConcernClick = (concern) => {
    navigate(`/patients/${concern.patient_id}`)
    onClose()
  }
  
  const handleReviewClick = (review) => {
    navigate(`/patients/${review.patient_id}`)
    onClose()
  }
  
  const handleViewAll = () => {
    navigate('/clinical-insights')
    onClose()
  }
  
  // ============================================================================
  // Computed Values
  // ============================================================================
  
  const totalUrgent = urgentConcerns.length
  const totalHigh = highConcerns.length
  const totalReviews = pendingReviews.length
  const hasAnyItems = totalUrgent > 0 || totalHigh > 0 || totalReviews > 0
  
  // Items to display (limited)
  const displayUrgent = urgentConcerns.slice(0, MAX_ITEMS_PER_SECTION)
  const displayHigh = highConcerns.slice(0, MAX_ITEMS_PER_SECTION)
  const displayReviews = pendingReviews.slice(0, MAX_ITEMS_PER_SECTION)
  
  // Remaining counts
  const remainingUrgent = totalUrgent - displayUrgent.length
  const remainingHigh = totalHigh - displayHigh.length
  const remainingReviews = totalReviews - displayReviews.length
  
  // ============================================================================
  // Render
  // ============================================================================
  
  if (!isOpen) return null
  
  return (
    <div 
      className="notifications-panel"
      ref={panelRef}
      role="dialog"
      aria-label="Notifications"
    >
      {/* Panel Header */}
      <div className="notifications-header">
        <h3>Notifications</h3>
        <button 
          className="btn-close-panel"
          onClick={onClose}
          aria-label="Close notifications"
        >
          <X size={18} />
        </button>
      </div>
      
      {/* Panel Content */}
      <div className="notifications-content">
        {loading ? (
          <div className="notifications-loading">
            <div className="spinner small"></div>
            <span>Loading...</span>
          </div>
        ) : error ? (
          <div className="notifications-error">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        ) : !hasAnyItems ? (
          <div className="notifications-empty">
            <Check size={32} />
            <p>All caught up!</p>
            <span>No pending notifications</span>
          </div>
        ) : (
          <>
            {/* Urgent Concerns Section */}
            {totalUrgent > 0 && (
              <div className="notification-section urgent">
                <SectionHeader title="URGENT" count={totalUrgent} />
                
                <div className="notification-items">
                  {displayUrgent.map(concern => (
                    <NotificationItem
                      key={concern.id}
                      icon={SEVERITY_CONFIG.urgent.icon}
                      iconColor={SEVERITY_CONFIG.urgent.color}
                      title={concern.description || concern.concern_type}
                      subtitle={concern.patient_name}
                      time={formatTime(concern.created_at)}
                      onClick={() => handleConcernClick(concern)}
                    />
                  ))}
                  
                  {remainingUrgent > 0 && (
                    <div className="more-items">
                      +{remainingUrgent} more urgent concerns
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* High Priority Concerns Section */}
            {totalHigh > 0 && (
              <div className="notification-section high">
                <SectionHeader title="HIGH PRIORITY" count={totalHigh} />
                
                <div className="notification-items">
                  {displayHigh.map(concern => (
                    <NotificationItem
                      key={concern.id}
                      icon={SEVERITY_CONFIG.high.icon}
                      iconColor={SEVERITY_CONFIG.high.color}
                      title={concern.description || concern.concern_type}
                      subtitle={concern.patient_name}
                      time={formatTime(concern.created_at)}
                      onClick={() => handleConcernClick(concern)}
                    />
                  ))}
                  
                  {remainingHigh > 0 && (
                    <div className="more-items">
                      +{remainingHigh} more high priority concerns
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Pending Reviews Section */}
            {totalReviews > 0 && (
              <div className="notification-section reviews">
                <SectionHeader title="PENDING REVIEWS" count={totalReviews} />
                
                <div className="notification-items">
                  {displayReviews.map(review => (
                    <NotificationItem
                      key={review.id}
                      icon={<MessageSquare size={14} />}
                      iconColor="var(--color-primary-teal)"
                      title={review.summary?.slice(0, 40) || 'Conversation'}
                      subtitle={review.patient_name}
                      time={formatTime(review.created_at || review.date)}
                      onClick={() => handleReviewClick(review)}
                    />
                  ))}
                  
                  {remainingReviews > 0 && (
                    <div className="more-items">
                      +{remainingReviews} more pending reviews
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Panel Footer */}
      <div className="notifications-footer">
        <button className="btn-view-all" onClick={handleViewAll}>
          View All in Clinical Insights
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  )
}

export default NotificationsPanel